import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { supabase } from '../supabase';
import { lazyWithRetry as lazy } from '../utils/lazyWithRetry';
import { resolveStoreMode, domainBehavior, normalizeHostname } from '../utils/storeDomains';

const Tienda = lazy(() => import('../pages/Tienda'));
const Rastreo = lazy(() => import('../pages/Rastreo'));

/**
 * Portero de dominio propio.
 *
 * Envuelve el árbol de rutas de la app. Si el navegador entró por un hostname
 * que NO es de la marca, monta únicamente el storefront de la tienda dueña de
 * ese dominio; el ERP no es alcanzable desde ahí.
 *
 * Vive en components/ y no en App.jsx a propósito: App.jsx está en el array
 * PROTECTED del sync a Gastify, así que todo lo que pueda salir de ahí sale.
 * Del lado de App.jsx el cambio son dos líneas.
 *
 * Spec: docs/ESPEC_DOMINIO_PROPIO_TIENDA.md
 */

// Hosts propios. No se listan los dominios de la marca uno por uno —eso
// obligaría a mantener una lista distinta en Impulza y en Gastify—: se listan
// los SUFIJOS de infraestructura, que son iguales en las dos apps.
// VITE_APP_HOSTS permite sumar el dominio propio de cada marca sin tocar código.
const OWN_HOST_SUFFIXES = [
  'localhost',
  '127.0.0.1',
  'web.app',          // impulza-erp.web.app y previews de Firebase
  'firebaseapp.com',
  ...String(import.meta.env.VITE_APP_HOSTS || '')
    .split(',')
    .map((h) => h.trim())
    .filter(Boolean),
];

// Necesario solo para redirigir un dominio suspendido de vuelta a la URL de la
// app. Si no está configurado, se sirve la tienda igual: dejar al comerciante
// sin canal de venta es peor que servirle la tienda un rato de más.
const APP_ORIGIN = String(import.meta.env.VITE_APP_ORIGIN || '').replace(/\/+$/, '');

const Cargando = () => (
  <div className="min-h-screen flex items-center justify-center bg-[#F2F9FF]">
    <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#3B82F6]" />
  </div>
);

/** Rastreo bajo dominio propio: la ruta cambia, el componente es el mismo. */
const RastreoPorDominio = () => {
  const { trackingCode } = useParams();
  return <Rastreo key={trackingCode} />;
};

export default function StorefrontGate({ children }) {
  const hostname = typeof window === 'undefined' ? '' : window.location.hostname;
  const esHostPropio = resolveStoreMode(hostname, OWN_HOST_SUFFIXES) === 'app';

  // En host propio no se consulta nada: la app arranca igual que siempre.
  const [estado, setEstado] = useState(esHostPropio ? 'app' : 'resolviendo');
  const [dominio, setDominio] = useState(null);

  useEffect(() => {
    if (esHostPropio) return;

    let vigente = true;

    (async () => {
      const { data, error } = await supabase
        .rpc('resolve_store_domain', { p_hostname: hostname })
        .maybeSingle();

      if (!vigente) return;

      // Consulta caída (Supabase o red): en un host ajeno NO se monta el ERP
      // con su login bajo el dominio del cliente; se ofrece reintentar.
      if (error) {
        setEstado('reintentar');
        return;
      }

      // No registrado: se monta la app normal. Cubre un dominio de la propia
      // marca que falte en VITE_APP_HOSTS. Bajo dominios de clientes esto no
      // llega: el Worker responde "en configuración" antes.
      if (!data) {
        setEstado('app');
        return;
      }

      const accion = domainBehavior(data);

      if (accion === 'render') {
        setDominio(data);
        setEstado('tienda');
        return;
      }

      if (accion === 'redirect') {
        // www -> apex, o suspendido -> URL de la app.
        const destino = data.redirect_to
          ? `https://${normalizeHostname(data.redirect_to)}`
          : (APP_ORIGIN && data.slug
            ? `${APP_ORIGIN}/tienda/${encodeURIComponent(data.slug)}`
            : null);

        if (destino) {
          window.location.replace(destino + window.location.pathname + window.location.search);
          return;
        }

        // Sin destino conocido: servir la tienda igual antes que romperla.
        setDominio(data);
        setEstado('tienda');
        return;
      }

      setEstado('app');
    })();

    return () => { vigente = false; };
  }, [esHostPropio, hostname]);

  if (estado === 'resolviendo') return <Cargando />;

  if (estado === 'reintentar') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F2F9FF] p-4 text-center">
        <div className="max-w-sm space-y-3">
          <p className="text-base font-bold text-slate-800">No pudimos cargar la tienda</p>
          <p className="text-sm text-slate-500">Revisá tu conexión e intentá de nuevo.</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  if (estado === 'tienda' && dominio) {
    return (
      <Routes>
        <Route path="/" element={<Tienda slugOverride={dominio.slug} />} />
        <Route path="/rastreo/:trackingCode" element={<RastreoPorDominio />} />
        {/* /sistema, /login, /landing y todo lo demás no existen bajo el
            dominio del cliente: el ERP no se alcanza desde acá. */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return children;
}
